import { Box, Button, Checkbox, Divider, Form, Grid, Text } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { actions, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { FC, Fragment, memo } from 'react';
import { useForm } from 'react-hook-form';
import { routingService } from 'services/routing';
import { RespondToInvitation, useGetUserContextRaw, useRespondToInvitation } from 'shared/data/webApp/v1';

import { invitesFieldName } from './InvitationsList.constants';
import { messages } from './InvitationsList.messages';
import { schema } from './InvitationsList.schema';
import { FormValues, InvitationsListProps } from './InvitationsList.types';

export const InvitationsList: FC<InvitationsListProps> = memo((props) => {
    const { pendingInvitations } = props;

    const dispatch = useDispatch();
    const form = useForm<FormValues>({
        defaultValues: {
            [invitesFieldName]: [],
        },
        resolver: zodResolver(schema),
    });

    const { mutateAsync: respondToInvitation } = useRespondToInvitation();
    const { refetch: refetchGetUserContext } = useGetUserContextRaw(undefined, {
        enabled: false,
    });

    const handleNext = form.handleSubmit(async (data) => {
        const invitesIds = data[invitesFieldName];

        const responses = invitesIds.reduce(
            (acc, invitationId) => {
                const invitation = pendingInvitations.find(({ companyId }) => companyId === invitationId);

                if (invitation) {
                    acc.push({
                        companyId: invitation.companyId,
                        response: domain.CompanyInvitationResponse.Accepted,
                    });
                }

                return acc;
            },
            [] as RespondToInvitation['body']['responses']
        );

        await respondToInvitation({
            body: {
                responses,
            },
        });

        const { data: context } = await refetchGetUserContext();

        if (context) {
            dispatch(actions.loadInitialAppData({ context }));
        }

        routingService.pushToDefaultPath();
    });

    return (
        <Form form={form} onSubmit={handleNext}>
            <Text font='headline' fontSize='medium' fontWeight='medium' spacing='0 0 40' color='midnight80'>
                {messages.title}
            </Text>

            <Checkbox.Group name={invitesFieldName} direction='column' size='medium' invalid={false} spacing='8'>
                {pendingInvitations.map((invitation) => (
                    <Fragment key={invitation.companyId}>
                        <label>
                            <Grid gridTemplateColumns='1fr auto' alignItems='center'>
                                <Grid gap={4}>
                                    <Text font='label' fontSize='medium' fontWeight='medium'>
                                        {invitation.companyName}
                                    </Text>

                                    <Text font='body' fontSize='medium' color='midnight70'>
                                        {messages.inviteDescription({
                                            name: selectors.user.getDisplayName(
                                                invitation.inviterUserFirstName,
                                                invitation.inviterUserLastName,
                                                invitation.inviterUserEmail
                                            ),
                                        })}
                                    </Text>
                                </Grid>

                                <Checkbox key={invitation.companyId} value={invitation.companyId}>
                                    {messages.accept}
                                </Checkbox>
                            </Grid>
                        </label>

                        <Divider />
                    </Fragment>
                ))}
            </Checkbox.Group>

            <Grid justifyContent='end'>
                <Box width={166} spacing='32 0 0'>
                    <Button
                        block
                        progress={form.formState.isSubmitting}
                        type='submit'
                        color='mint80'
                        size='large'
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        {messages.accept}
                    </Button>
                </Box>
            </Grid>
        </Form>
    );
});

InvitationsList.displayName = 'InvitationsList';
